import { captureException } from '@sentry/react';
import { isAxiosError } from 'axios';

/**
 * Captures a billing error in Sentry without forwarding the raw AxiosError object,
 * which carries request config and headers (including Authorization tokens).
 * For Axios failures, a synthetic Error is captured with only safe context:
 * HTTP status, method, and request-id. URL is intentionally omitted — billing
 * endpoints can embed user identifiers in path segments.
 * @param error - The caught error value (any shape).
 * @param action - Slug describing the billing operation that failed (e.g. `'cancel-subscription'`).
 */
export const captureBillingError = (error: unknown, action: string) => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const method = error.config?.method?.toUpperCase();
    const requestId = error.config?.headers?.['Request-id'] as
      | string
      | undefined;

    captureException(new Error(error.message), {
      tags: {
        area: 'billing',
        action,
        http_status: String(status ?? 'unknown'),
      },
      extra: { status, method, requestId },
    });
  } else {
    captureException(error, { tags: { area: 'billing', action } });
  }
};
